import { useNavigate, useParams } from 'react-router-dom';
import useApiCall from '../../../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../../../hooks/useGetGeneralAppData';
import { useCallback, useState } from 'react';
import VehiclesService from '../../../../../../../services/affreightment/VehiclesService';
import { toast } from 'react-toastify';
import { ErrorsInterface } from '../../../../../../../hooks/useErrors';
import { MultiValue } from 'react-select';

interface UseFormSubmitInterface {
  model: string| number;
  transportType: { value: number, label: string };
  vehicleCode: string;
  brand: string;
  capacity: string | number;
  consumption: string | number;
  errors: ErrorsInterface[];
  affreightmentLines: MultiValue<{ value: string, label: string }>;
}

export default function useFormSubmit({
  model,
  transportType,
  vehicleCode,
  brand,
  capacity,
  consumption,
  errors,
  affreightmentLines,
}: UseFormSubmitInterface) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { apiCall } = useApiCall();
  const { userCode, userProfile, userName, companyCode } = useGetGeneralAppData();
  const navigate = useNavigate();
  const { vehicleId } = useParams();

  const isFormValid = (model && transportType && vehicleCode && brand && capacity && consumption
    && errors.length === 0);

  const createLine = useCallback(async () => {
    await apiCall({
      apiToCall: VehiclesService.createVehicle,
      queryParams: { codUsu: userCode, perfilUsu: userProfile, usuario: userName, codEmpresa: companyCode },
      reqBody: JSON.stringify({
        modelo: model,
        tipoTransp: transportType.value,
        codVeiculo: vehicleCode,
        marca: brand,
        capacidade: capacity,
        consumo: consumption,
        linhasFretado: affreightmentLines.map((line) => (
          { routeID: line.value }
        )),
      }),
      onStartLoad: () => setIsSubmitting(true),
      onEndLoad: () => setIsSubmitting(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível criar o veículo. Por favor, tente novamente (${response.msg})`);
          return;
        }
        toast.success('O veículo foi criado com sucesso!');
        navigate('/vehicles');
      },
      catchMessage: 'Não foi possível criar o veículo. Por favor, tente novamente',
    });
  }, [affreightmentLines, apiCall, brand, capacity, companyCode, consumption, model, navigate, transportType, userCode, userName, userProfile, vehicleCode]);

  const editLine = useCallback(async () => {
    await apiCall({
      apiToCall: VehiclesService.updateVehicle,
      queryParams: {
        codUsu: userCode,
        usuario: userName,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        codVeiculoSis: vehicleId,
        consumo: consumption,
      },
      reqBody: JSON.stringify(
        affreightmentLines.map((line) => (
          { routeID: line.value }
        )),
      ),
      onStartLoad: () => setIsSubmitting(true),
      onEndLoad: () => setIsSubmitting(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível editar o veículo. Por favor, tente novamente (${response.msg})`);
          return;
        }
        toast.success('Veículo editado com sucesso!');
        navigate('/vehicles');
      },
      catchMessage: 'Não foi possível editar o veículo. Por favor, tente novamente',
    });
  }, [affreightmentLines, apiCall, companyCode, consumption, navigate, userCode, userName, userProfile, vehicleId]);

  return {
    isFormValid,
    createLine,
    editLine,
    isSubmitting,
  };
}
